import { useEffect } from 'react'

interface JsonLdProps {
  /**
   * The schema.org payload to inject. The component does NOT validate the
   * shape — that's the caller's responsibility. Use well-known types like
   * `Organization`, `BreadcrumbList`, `Article`, etc.
   */
  data: Record<string, unknown>
  /**
   * Unique identifier used to scope a <script> tag to a single JSON-LD
   * payload. Useful when a page emits more than one schema block — pass a
   * distinct id for each so cleanup only removes the right one.
   *
   * Defaults to `'default'`.
   */
  id?: string
}

/**
 * JsonLd — injects a <script type="application/ld+json"> tag into document.head
 * for structured data. Mirrors the ownership discipline of `usePageSEO`:
 * the tag is scoped via `data-json-ld` and removed on unmount so nothing
 * leaks across route changes.
 *
 * Renders nothing. Call from any page component where structured data is
 * needed (Home → Organization; detail pages → BreadcrumbList).
 */
export function JsonLd({ data, id = 'default' }: JsonLdProps) {
  useEffect(() => {
    const payload = JSON.stringify(data)
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-json-ld', id)
    script.text = payload
    document.head.appendChild(script)

    return () => {
      script.remove()
    }
  }, [data, id])

  return null
}
